import React, { useState, useEffect } from 'react';
import { authFetchInit, getAuthHeaders, hasAuthSession } from '../utils/authSession';
import {
  Box,
  Typography,
  Card,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Avatar,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Tabs,
  Tab,
  TextField,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Security as SecurityIcon,
  Cloud as CloudIcon,
  Settings as SettingsIcon,
  Shield as ShieldIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Sync as SyncIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import SSOAttributeSettings from '../components/SSOAttributeSettings';
import { API_ROOT } from '../config/apiConfig';
import { FORM_PLACEHOLDERS } from '../constants/formPlaceholders';
import { UI_TOKENS } from '../constants/uiTokens';
import { PAGE, SELECT_SX, MENU_PROPS, CARD_SX, TABS_SX } from '../constants/pageStyles';

const SELECTED_IDP_STORAGE_KEY = 'provysn_security_selected_idp';

const SecuritySimple: React.FC = () => {
  const { user } = useAuth();
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedIDP, setSelectedIDP] = useState<string>('azure');
  const [defaultIDPDetected, setDefaultIDPDetected] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isEditingEntra, setIsEditingEntra] = useState<boolean>(false);
  const [entraConfig, setEntraConfig] = useState({
    tenantId: '',
    clientId: '',
    clientSecret: ''
  });
  const [isTestingConnection, setIsTestingConnection] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<{
    status: 'idle' | 'success' | 'error';
    message: string;
  }>({ status: 'idle', message: '' });

  // Okta state
  const [oktaConfig, setOktaConfig] = useState({ org_url: '', api_token: '' });
  const [isEditingOkta, setIsEditingOkta] = useState(false);
  const [isTestingOkta, setIsTestingOkta] = useState(false);
  const [isSavingOkta, setIsSavingOkta] = useState(false);
  const [oktaConfigured, setOktaConfigured] = useState(false);
  const [entraConfigured, setEntraConfigured] = useState(false);
  const [oktaConnectionStatus, setOktaConnectionStatus] = useState<{ status: 'idle' | 'success' | 'error'; message: string }>({ status: 'idle', message: '' });
  const [oktaCapabilities, setOktaCapabilities] = useState<{ label: string; key: string }[]>([]);
  const [isSyncingPolicies, setIsSyncingPolicies] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{ total: number; lastSync: string | null; nextSync: string | null; counts: any[] } | null>(null);
  const [scheduleConfig, setScheduleConfig] = useState<{ is_enabled: boolean; frequency: string; sync_time_gmt: string }>({ is_enabled: false, frequency: 'daily', sync_time_gmt: '00:00' });
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [oktaSaveStatus, setOktaSaveStatus] = useState<{ status: 'idle' | 'success' | 'error'; message: string }>({ status: 'idle', message: '' });
  const [saveStatus, setSaveStatus] = useState<{
    status: 'idle' | 'success' | 'error';
    message: string;
  }>({ status: 'idle', message: '' });
  const [secretSharingSettings, setSecretSharingSettings] = useState({
    shareWithOwner: true,
    shareWithSSOAdmin: true,
    storeInVault: false
  });
  const [automationSettings, setAutomationSettings] = useState({
    autoOnboarding: false,
    ssoAdminApproval: true,
    emailNotifications: true,
    serviceNowIntegration: false
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);

    const savedIdp = sessionStorage.getItem(SELECTED_IDP_STORAGE_KEY);
    if (savedIdp) {
      setSelectedIDP(savedIdp);
    }

    // Load settings
    loadSecretSharingSettings();
    loadAutomationSettings();
    loadOktaConfig();
    loadSyncStatus();
    if (!savedIdp) {
      detectConfiguredIDP();
    }

    return () => clearTimeout(timer);
  }, []);

  // Load saved Entra configuration on component mount
  useEffect(() => {
    const loadEntraConfig = async () => {
      try {
                if (!hasAuthSession()) return;

        const response = await fetch(`${API_ROOT}/entra/config`, authFetchInit());

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setEntraConfig({
              tenantId: result.data.tenant_id || '',
              clientId: result.data.client_id || '',
              clientSecret: result.data.client_secret ? '••••••••••••••••••••••••••••••••' : '',
            });
            setEntraConfigured(Boolean(result.data.is_configured || result.data.has_client_secret));
          } else {
            setEntraConfig({ tenantId: '', clientId: '', clientSecret: '' });
            setEntraConfigured(false);
          }
        }
      } catch (error) {
        console.error('Error loading Entra config:', error);
      }
    };

    loadEntraConfig();
  }, []);

  const handleEditEntraConfig = () => {
    setIsEditingEntra(true);
    setSaveStatus({ status: 'idle', message: '' });
  };

  const handleSaveEntraConfig = async () => {
    setSaveStatus({ status: 'idle', message: '' });
    
    try {
            if (!hasAuthSession()) {
        setSaveStatus({ status: 'error', message: 'No authentication token found' });
        return;
      }

      const isMasked = Boolean(entraConfig.clientSecret?.includes('•'));
      const payload: Record<string, string> = {
        tenant_id: entraConfig.tenantId,
        client_id: entraConfig.clientId,
      };
      if (!isMasked && entraConfig.clientSecret) {
        payload.client_secret = entraConfig.clientSecret;
      }

      const response = await fetch(`${API_ROOT}/entra/config`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSaveStatus({ status: 'success', message: 'Configuration saved successfully!' });
        setIsEditingEntra(false);
        setEntraConfigured(true);
        if (result.data?.client_secret) {
          setEntraConfig((prev) => ({
            ...prev,
            clientSecret: '••••••••••••••••••••••••••••••••',
          }));
        }
        setTimeout(() => {
          setSaveStatus({ status: 'idle', message: '' });
        }, 3000);
      } else {
        setSaveStatus({ status: 'error', message: result.message || 'Failed to save configuration' });
      }
    } catch (error) {
      setSaveStatus({ status: 'error', message: 'Network error. Please try again.' });
    }
  };

  const handleCancelEntraEdit = () => {
    setIsEditingEntra(false);
    setSaveStatus({ status: 'idle', message: '' });
  };

  const handleEntraConfigChange = (field: string, value: string) => {
    setEntraConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Okta handlers
  const detectConfiguredIDP = async () => {
    try {
            if (!hasAuthSession()) return;

      // Check Okta first
      const oktaRes = await fetch(`${API_ROOT}/okta/config`, authFetchInit());
      if (oktaRes.ok) {
        const oktaResult = await oktaRes.json();
        if (oktaResult.success && oktaResult.data?.is_configured) {
          setSelectedIDP('okta');
          setDefaultIDPDetected(true);
          return;
        }
      }

      // Check Entra/Azure
      const entraRes = await fetch(`${API_ROOT}/entra/config`, authFetchInit());
      if (entraRes.ok) {
        const entraResult = await entraRes.json();
        if (entraResult.success && entraResult.data?.is_configured) {
          setSelectedIDP('azure');
          setEntraConfigured(true);
          setDefaultIDPDetected(true);
          return;
        }
      }
    } catch (error) {
      console.error('Error detecting configured IDP:', error);
    }
  };

  const loadOktaConfig = async () => {
    try {
            if (!hasAuthSession()) return;
      const response = await fetch(`${API_ROOT}/okta/config`, authFetchInit());
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setOktaConfig({ org_url: result.data.org_url || '', api_token: '' });
          setOktaConfigured(Boolean(result.data.is_configured));
        }
      }
    } catch (error) {
      console.error('Error loading Okta config:', error);
    }
  };

  const handleSaveOktaConfig = async () => {
    setIsSavingOkta(true);
    setOktaSaveStatus({ status: 'idle', message: '' });
    try {
      const body: Record<string, string> = { org_url: oktaConfig.org_url };
      if (oktaConfig.api_token && !oktaConfig.api_token.includes('•')) {
        body.api_token = oktaConfig.api_token;
      }
      const response = await fetch(`${API_ROOT}/okta/config`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });
      const result = await response.json();
      if (result.success) {
        setOktaSaveStatus({ status: 'success', message: 'Okta configuration saved successfully!' });
        setIsEditingOkta(false);
        setOktaConfigured(true);
        setOktaConfig(prev => ({ ...prev, api_token: '' }));
        setTimeout(() => setOktaSaveStatus({ status: 'idle', message: '' }), 3000);
      } else {
        setOktaSaveStatus({ status: 'error', message: result.message || 'Failed to save configuration' });
      }
    } catch (error) {
      setOktaSaveStatus({ status: 'error', message: 'Network error. Please try again.' });
    } finally {
      setIsSavingOkta(false);
    }
  };

  const handleTestOktaConnection = async () => {
    setIsTestingOkta(true);
    setOktaConnectionStatus({ status: 'idle', message: '' });
    try {
      // Only send api_token if it's a real value (not the masked placeholder)
      const isMasked = Boolean(oktaConfig.api_token?.includes('•'));
      const body: Record<string, string> = { org_url: oktaConfig.org_url || '' };
      if (!isMasked && oktaConfig.api_token) body.api_token = oktaConfig.api_token;

      const response = await fetch(`${API_ROOT}/okta/test-connection`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });
      const raw = await response.text();
      let result: { success?: boolean; message?: string; error?: string; data?: { capabilities?: unknown } };
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch {
        setOktaConnectionStatus({
          status: 'error',
          message: raw?.slice(0, 200) || `Invalid response (HTTP ${response.status})`
        });
        return;
      }
      const msg = result.message || result.error || (!response.ok ? `HTTP ${response.status}` : '');
      setOktaConnectionStatus({
        status: result.success ? 'success' : 'error',
        message: msg || (result.success ? 'OK' : 'Connection failed')
      });
      const caps = result.data?.capabilities;
      if (result.success && Array.isArray(caps)) {
        setOktaCapabilities(caps as { label: string; key: string }[]);
      }
    } catch (error) {
      setOktaConnectionStatus({ status: 'error', message: 'Network error. Could not reach backend.' });
    } finally {
      setIsTestingOkta(false);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const response = await fetch(`${API_ROOT}/okta/sync-status`, authFetchInit());
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          const { schedule, recent_logs, policy_counts } = result.data;
          const total = policy_counts.reduce((sum: number, r: any) => sum + parseInt(r.total), 0);
          setSyncStatus({
            total,
            lastSync: recent_logs[0]?.synced_at || null,
            nextSync: schedule.next_sync_at || null,
            counts: policy_counts.map((row: { type: string; total: string; idp_source?: string }) => ({
              type: row.type,
              total: parseInt(row.total, 10),
              idp_source: row.idp_source || 'okta',
            })),
          });
          setScheduleConfig({
            is_enabled: schedule.is_enabled || false,
            frequency: schedule.frequency || 'daily',
            sync_time_gmt: schedule.sync_time_gmt || '00:00'
          });
        }
      }
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  const handleSyncNow = async () => {
    setIsSyncingPolicies(true);
    setOktaConnectionStatus({ status: 'idle', message: '' });
    const provider = selectedIDP === 'azure' ? 'azure' : selectedIDP === 'okta' ? 'okta' : 'all';
    try {
      const response = await fetch(`${API_ROOT}/okta/sync-policies`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ provider }),
      });
      const result = await response.json();
      setOktaConnectionStatus({
        status: result.success ? 'success' : 'error',
        message: result.message
      });
      if (result.success) await loadSyncStatus();
    } catch (error) {
      setOktaConnectionStatus({ status: 'error', message: 'Network error during sync.' });
    } finally {
      setIsSyncingPolicies(false);
    }
  };

  const handleSaveSchedule = async () => {
    setIsSavingSchedule(true);
    try {
      const response = await fetch(`${API_ROOT}/okta/sync-schedule`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(scheduleConfig)
      });
      const result = await response.json();
      setOktaConnectionStatus({
        status: result.success ? 'success' : 'error',
        message: result.message
      });
      if (result.success) setScheduleDialogOpen(false);
    } catch (error) {
      setOktaConnectionStatus({ status: 'error', message: 'Failed to save schedule.' });
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const loadSecretSharingSettings = async () => {
    try {
            if (!hasAuthSession()) return;

      const response = await fetch(`${API_ROOT}/entra/secret-sharing-settings`, authFetchInit());

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('Loaded secret sharing settings:', data.data);
          setSecretSharingSettings({
            shareWithOwner: data.data.share_with_owner !== false,
            shareWithSSOAdmin: data.data.share_with_sso_admin !== false,
            storeInVault: Boolean(data.data.store_in_vault)
          });
        }
      }
    } catch (error) {
      console.error('Error loading secret sharing settings:', error);
    }
  };

  const loadAutomationSettings = async () => {
    try {
            if (!hasAuthSession()) return;

      const response = await fetch(`${API_ROOT}/entra/automation-settings`, authFetchInit());

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAutomationSettings({
            autoOnboarding: Boolean(data.data.auto_onboarding),
            ssoAdminApproval: data.data.sso_admin_approval !== false,
            emailNotifications: data.data.email_notifications !== false,
            serviceNowIntegration: Boolean(data.data.service_now_integration)
          });
        }
      }
    } catch (error) {
      console.error('Error loading automation settings:', error);
    }
  };

  const handleSecretSharingChange = async (setting: string, value: boolean) => {
    console.log('SecuritySimple: Toggle changed - setting:', setting, 'value:', value);
    const newSettings = { ...secretSharingSettings, [setting]: value };
    setSecretSharingSettings(newSettings);

    try {
            if (!hasAuthSession()) return;

      console.log('SecuritySimple: Saving secret sharing settings:', newSettings);
      const response = await fetch(`${API_ROOT}/entra/secret-sharing-settings`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          settings: {
            share_with_owner: newSettings.shareWithOwner,
            share_with_sso_admin: newSettings.shareWithSSOAdmin,
            store_in_vault: newSettings.storeInVault
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('SecuritySimple: Secret sharing settings saved successfully:', data);
      } else {
        // Revert on failure
        setSecretSharingSettings(secretSharingSettings);
        console.error('Failed to save secret sharing settings');
      }
    } catch (error) {
      // Revert on error
      setSecretSharingSettings(secretSharingSettings);
      console.error('Error saving secret sharing settings:', error);
    }
  };

  const handleAutomationChange = async (setting: string, value: boolean) => {
    const newSettings = { ...automationSettings, [setting]: value };
    setAutomationSettings(newSettings);

    try {
            if (!hasAuthSession()) return;

      const response = await fetch(`${API_ROOT}/entra/automation-settings`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          settings: {
            auto_onboarding: newSettings.autoOnboarding,
            sso_admin_approval: newSettings.ssoAdminApproval,
            email_notifications: newSettings.emailNotifications,
            service_now_integration: false
          }
        })
      });

      if (!response.ok) {
        // Revert on failure
        setAutomationSettings(automationSettings);
        console.error('Failed to save automation settings');
      }
    } catch (error) {
      // Revert on error
      setAutomationSettings(automationSettings);
      console.error('Error saving automation settings:', error);
    }
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus({ status: 'idle', message: '' });

    try {
      console.log('Testing connection with:', {
        tenantId: entraConfig.tenantId,
        clientId: entraConfig.clientId,
        clientSecret: entraConfig.clientSecret ? 'Present' : 'Missing'
      });

      const isMasked = Boolean(entraConfig.clientSecret?.includes('•'));
      const body: Record<string, string> = {
        tenantId: entraConfig.tenantId,
        clientId: entraConfig.clientId,
      };
      if (!isMasked && entraConfig.clientSecret) {
        body.clientSecret = entraConfig.clientSecret;
      }

      const response = await fetch(`${API_ROOT}/entra/test-connection`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });

      const result = await response.json();
      console.log('Test connection response:', result);

      if (response.ok && result.success) {
        setConnectionStatus({
          status: 'success',
          message: 'Connection successful! Graph API access verified.'
        });
      } else {
        setConnectionStatus({
          status: 'error',
          message: result.message || 'Connection failed. Please check your credentials.'
        });
      }
    } catch (error) {
      console.error('Test connection error:', error);
      setConnectionStatus({
        status: 'error',
        message: 'Failed to test connection. Please try again.'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const getFilteredSyncCounts = (source: 'okta' | 'azure') =>
    (syncStatus?.counts || []).filter((c) => c.idp_source === source);

  const formatPolicyCountLabel = (count: { type: string; total: number; idp_source?: string }) => {
    const provider = count.idp_source === 'azure' ? 'Entra' : 'Okta';
    return `${provider} · ${count.type.replace(/_/g, ' ')}: ${count.total}`;
  };

  const renderPolicySyncPanel = (helperText: string, emptyHint: string, source: 'okta' | 'azure') => {
    const filteredCounts = getFilteredSyncCounts(source);
    const filteredTotal = filteredCounts.reduce((sum, c) => sum + c.total, 0);

    return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography sx={PAGE.subsectionTitle}>Policy Sync</Typography>
          <Typography sx={PAGE.helper}>{helperText}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={isSyncingPolicies ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={handleSyncNow}
            disabled={isSyncingPolicies}
          >
            {isSyncingPolicies ? 'Syncing...' : 'Sync Now'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ScheduleIcon />}
            onClick={() => setScheduleDialogOpen(true)}
          >
            {scheduleConfig.is_enabled ? `Scheduled (${scheduleConfig.frequency})` : 'Schedule Sync'}
          </Button>
        </Box>
      </Box>

      {syncStatus && filteredTotal > 0 && (
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ ...PAGE.subsectionTitle }}>
              {filteredTotal} policies synced
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              {syncStatus.lastSync && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Last sync: {new Date(syncStatus.lastSync).toLocaleString()}
                </Typography>
              )}
              {syncStatus.nextSync && scheduleConfig.is_enabled && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Next sync: {new Date(syncStatus.nextSync).toUTCString()}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filteredCounts.map((c) => (
              <Chip
                key={`${c.idp_source || source}-${c.type}`}
                label={formatPolicyCountLabel(c)}
                size="small"
                variant="outlined"
                color="info"
              />
            ))}
          </Box>
        </Box>
      )}
      {filteredTotal === 0 && (
        <Typography sx={{ ...PAGE.helper, fontStyle: 'italic' }}>
          {emptyHint}
        </Typography>
      )}
    </Box>
  );
  };

  const canManageSecurity = () => {
    return user?.role === 'super_admin';
  };

  if (!canManageSecurity()) {
    return (
      <Box sx={PAGE.padding}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <SecurityIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Typography sx={PAGE.title}>Security Settings</Typography>
        </Box>
        <Alert severity="error" sx={{ '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }}>
          Access denied. Super admin role required to access security settings.
        </Alert>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={PAGE.loader}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={28} sx={{ mb: 1.5 }} />
          <Typography sx={PAGE.body}>Loading security settings…</Typography>
        </Box>
      </Box>
    );
  }

  const renderSSOTab = () => (
    <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5, width: 36, height: 36 }}>
              <CloudIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography sx={PAGE.subsectionTitle}>Identity Provider Configuration</Typography>
              <Typography sx={PAGE.helper}>
                Select and configure your primary identity provider
              </Typography>
            </Box>
          </Box>

        <Box sx={{ pt: 2.5 }}>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ p: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Okta</Typography>
                <Typography sx={{ fontSize: '0.8rem' }}>
                  {oktaConnectionStatus.status === 'error' ? 'Failing' : oktaConfigured ? 'Healthy' : 'Not configured'}
                </Typography>
                {oktaConnectionStatus.status === 'error' && (
                  <Typography sx={{ fontSize: '0.75rem', color: 'error.main' }}>{oktaConnectionStatus.message}</Typography>
                )}
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ p: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Entra</Typography>
                <Typography sx={{ fontSize: '0.8rem' }}>{entraConfigured ? 'Healthy' : 'Not configured'}</Typography>
              </Card>
            </Grid>
          </Grid>
          <Box sx={{ mb: 4 }}>
            {defaultIDPDetected && (
              <Alert severity="info" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
                Showing your configured Identity Provider: <strong>{selectedIDP === 'okta' ? 'Okta' : 'Azure AD / Microsoft Entra ID'}</strong>
              </Alert>
            )}
            <FormControl fullWidth sx={{ mb: 3, ...SELECT_SX }}>
              <InputLabel>Select Identity Provider</InputLabel>
              <Select
                value={selectedIDP}
                label="Select Identity Provider"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedIDP(value);
                  sessionStorage.setItem(SELECTED_IDP_STORAGE_KEY, value);
                  if (value === 'azure' || value === 'okta') {
                    try {
                      localStorage.setItem('turboiam-idp-platform', value);
                    } catch {
                      /* ignore */
                    }
                  }
                  setDefaultIDPDetected(false);
                }}
                MenuProps={MENU_PROPS}
              >
                <MenuItem value="azure">
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Avatar sx={{ bgcolor: '#0078d4', mr: 2, width: 24, height: 24 }}>
                      <SecurityIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>Azure AD / Microsoft Entra ID</Box>
                    {entraConfigured && <Chip label="Active" color="success" size="small" sx={{ ml: 1 }} />}
                  </Box>
                </MenuItem>
                <MenuItem value="okta">
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Avatar sx={{ bgcolor: '#007dc3', mr: 2, width: 24, height: 24 }}>
                      <CloudIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>Okta</Box>
                    {oktaConfigured && <Chip label="Active" color="success" size="small" sx={{ ml: 1 }} />}
                  </Box>
                </MenuItem>
                <MenuItem value="google">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#4285f4', mr: 2, width: 24, height: 24 }}>
                      <CloudIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    Google Workspace
                  </Box>
                </MenuItem>
                <MenuItem value="ping">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#00a651', mr: 2, width: 24, height: 24 }}>
                      <ShieldIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    Ping Identity
                  </Box>
                </MenuItem>
                <MenuItem value="generic_saml">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#6c757d', mr: 2, width: 24, height: 24 }}>
                      <SettingsIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    Generic SAML Provider
                  </Box>
                </MenuItem>
                <MenuItem value="generic_oidc">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: '#6c757d', mr: 2, width: 24, height: 24 }}>
                      <SettingsIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    Generic OIDC Provider
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {selectedIDP === 'azure' && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#0078d4', mr: 2, width: 36, height: 36 }}>
                    <SecurityIcon />
                  </Avatar>
                  <Box>
                    <Typography sx={PAGE.subsectionTitle}>Microsoft Entra ID Configuration</Typography>
                    <Typography sx={PAGE.helper}>
                      Connect Microsoft Entra ID to enable SSO onboarding and risk policy sync
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={entraConfigured ? 'Configured' : 'Not Configured'}
                    color={entraConfigured ? 'success' : 'warning'}
                    size="small"
                  />
                  {!isEditingEntra && (
                    <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={handleEditEntraConfig}>
                      {entraConfigured ? 'Edit' : 'Configure'}
                    </Button>
                  )}
                </Box>
              </Box>

              {isEditingEntra && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You are editing the Entra ID configuration. Save to apply changes.
                </Alert>
              )}

              {connectionStatus.status !== 'idle' && (
                <Alert
                  severity={connectionStatus.status === 'success' ? 'success' : 'error'}
                  sx={{ mb: 2 }}
                  onClose={() => setConnectionStatus({ status: 'idle', message: '' })}
                >
                  {connectionStatus.message}
                </Alert>
              )}
              {saveStatus.status !== 'idle' && (
                <Alert
                  severity={saveStatus.status === 'success' ? 'success' : 'error'}
                  sx={{ mb: 2 }}
                  onClose={() => setSaveStatus({ status: 'idle', message: '' })}
                >
                  {saveStatus.message}
                </Alert>
              )}
              {oktaConnectionStatus.status !== 'idle' && (
                <Alert
                  severity={oktaConnectionStatus.status === 'success' ? 'success' : 'error'}
                  sx={{ mb: 2 }}
                  onClose={() => setOktaConnectionStatus({ status: 'idle', message: '' })}
                >
                  {oktaConnectionStatus.message}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tenant ID"
                    value={entraConfig.tenantId}
                    onChange={(e) => handleEntraConfigChange('tenantId', e.target.value)}
                    disabled={!isEditingEntra}
                    helperText="Your Microsoft Entra tenant (directory) ID"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Application (Client) ID"
                    value={entraConfig.clientId}
                    onChange={(e) => handleEntraConfigChange('clientId', e.target.value)}
                    disabled={!isEditingEntra}
                    helperText="App registration used for Graph API access (not your Provysn login user)"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Client Secret"
                    value={entraConfig.clientSecret}
                    onChange={(e) => handleEntraConfigChange('clientSecret', e.target.value)}
                    disabled={!isEditingEntra}
                    type={isEditingEntra ? 'text' : 'password'}
                    helperText="Client secret for the app registration (required for policy sync and test connection)"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      color="info"
                      onClick={handleTestConnection}
                      disabled={isTestingConnection || !entraConfig.tenantId || !entraConfig.clientId}
                      startIcon={isTestingConnection ? <CircularProgress size={16} /> : <ShieldIcon />}
                    >
                      {isTestingConnection ? 'Testing...' : 'Test Connection'}
                    </Button>
                    {isEditingEntra && (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSaveEntraConfig}
                          disabled={!entraConfig.tenantId || !entraConfig.clientId || (!entraConfigured && !entraConfig.clientSecret)}
                          startIcon={<SaveIcon />}
                        >
                          Save Configuration
                        </Button>
                        <Button variant="outlined" color="inherit" startIcon={<CancelIcon />} onClick={handleCancelEntraEdit}>
                          Cancel
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {entraConfigured &&
                renderPolicySyncPanel(
                  'Pull conditional access, authentication strength, and security policies from Microsoft Entra into Provysn',
                  'No Entra policies synced yet. Click "Sync Now" to pull policies from Microsoft Entra.',
                  'azure'
                )}
            </Box>
          )}

          {selectedIDP === 'okta' && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#007dc3', mr: 2, width: 36, height: 36 }}>
                    <CloudIcon />
                  </Avatar>
                  <Box>
                    <Typography sx={PAGE.subsectionTitle}>Okta Configuration</Typography>
                    <Typography sx={PAGE.helper}>
                      Connect your Okta tenant to enable SSO onboarding and risk policy sync
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={oktaConfigured ? 'Configured' : 'Not Configured'}
                    color={oktaConfigured ? 'success' : 'warning'}
                    size="small"
                  />
                  {!isEditingOkta && (
                    <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setIsEditingOkta(true)}>
                      {oktaConfigured ? 'Edit' : 'Configure'}
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Status alerts */}
              {oktaConnectionStatus.status !== 'idle' && (
                <Alert severity={oktaConnectionStatus.status === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}
                  onClose={() => setOktaConnectionStatus({ status: 'idle', message: '' })}>
                  {oktaConnectionStatus.message}
                </Alert>
              )}
              {oktaSaveStatus.status !== 'idle' && (
                <Alert severity={oktaSaveStatus.status === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}
                  onClose={() => setOktaSaveStatus({ status: 'idle', message: '' })}>
                  {oktaSaveStatus.message}
                </Alert>
              )}

              {/* Config form */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Okta Org URL"
                    placeholder={FORM_PLACEHOLDERS.oktaOrgUrl}
                    value={oktaConfig.org_url}
                    onChange={e => setOktaConfig(prev => ({ ...prev, org_url: e.target.value }))}
                    disabled={!isEditingOkta}
                    helperText="Your Okta organization URL (e.g. https://dev-123456.okta.com)"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Token"
                    type="password"
                    autoComplete="new-password"
                    placeholder={oktaConfigured ? 'Leave blank to keep the stored token' : 'Paste an Okta API token'}
                    value={oktaConfig.api_token}
                    onChange={e => setOktaConfig(prev => ({ ...prev, api_token: e.target.value }))}
                    disabled={!isEditingOkta}
                    helperText={
                      oktaConfigured
                        ? 'A token is stored on the server and is not loaded into this browser. Enter a new token only to replace it.'
                        : 'Okta API token with the permissions this connection needs (Security → API → Tokens).'
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      color="info"
                      onClick={handleTestOktaConnection}
                      disabled={isTestingOkta || !oktaConfig.org_url || (!oktaConfig.api_token && !oktaConfigured)}
                      startIcon={isTestingOkta ? <CircularProgress size={16} /> : <ShieldIcon />}
                    >
                      {isTestingOkta ? 'Testing...' : 'Test Connection'}
                    </Button>
                    {isEditingOkta && (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSaveOktaConfig}
                          disabled={isSavingOkta || !oktaConfig.org_url || (!oktaConfig.api_token && !oktaConfigured)}
                          startIcon={isSavingOkta ? <CircularProgress size={16} /> : <SaveIcon />}
                        >
                          {isSavingOkta ? 'Saving...' : 'Save Configuration'}
                        </Button>
                        <Button variant="outlined" color="inherit" startIcon={<CancelIcon />} onClick={() => { setIsEditingOkta(false); loadOktaConfig(); }}>
                          Cancel
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Confirmed Capabilities */}
              {oktaCapabilities.length > 0 && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography sx={{ ...PAGE.subsectionTitle, mb: 1 }}>Confirmed Capabilities</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {oktaCapabilities.map(cap => (
                      <Chip key={cap.key} label={cap.label} color="success" size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              {oktaConfigured &&
                renderPolicySyncPanel(
                  'Pull authentication, login, global and all policy types from Okta into Provysn',
                  'No Okta policies synced yet. Click "Sync Now" to pull policies from Okta.',
                  'okta'
                )}
            </Box>
          )}

          <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Schedule Policy Sync</DialogTitle>
            <DialogContent>
              <Typography sx={{ ...PAGE.helper, mb: 2 }}>
                Automatically pull policies from all configured identity providers on a recurring schedule.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={scheduleConfig.is_enabled}
                    onChange={e => setScheduleConfig(prev => ({ ...prev, is_enabled: e.target.checked }))}
                  />
                }
                label="Enable scheduled sync"
              />
              <FormControl fullWidth sx={{ mt: 2 }} size="small" disabled={!scheduleConfig.is_enabled}>
                <InputLabel>Frequency</InputLabel>
                <Select
                  label="Frequency"
                  value={scheduleConfig.frequency}
                  onChange={e => setScheduleConfig(prev => ({ ...prev, frequency: e.target.value }))}
                >
                  <MenuItem value="hourly">Every Hour</MenuItem>
                  <MenuItem value="daily">Every Day</MenuItem>
                  <MenuItem value="weekly">Every Week</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Sync Time (GMT)"
                type="time"
                value={scheduleConfig.sync_time_gmt}
                onChange={e => setScheduleConfig(prev => ({ ...prev, sync_time_gmt: e.target.value }))}
                disabled={!scheduleConfig.is_enabled || scheduleConfig.frequency === 'hourly'}
                helperText={
                  scheduleConfig.frequency === 'hourly'
                    ? 'Time not applicable for hourly sync'
                    : `Sync will run at ${scheduleConfig.sync_time_gmt} GMT`
                }
                sx={{ mt: 2 }}
                InputLabelProps={{ shrink: true }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSaveSchedule}
                disabled={isSavingSchedule}
                startIcon={isSavingSchedule ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              >
                Save Schedule
              </Button>
            </DialogActions>
          </Dialog>

          {selectedIDP === 'google' && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#4285f4', mr: 2, width: 36, height: 36 }}>
                    <CloudIcon />
                  </Avatar>
                  <Box>
                    <Typography sx={PAGE.subsectionTitle}>
                      Google Workspace Configuration
                    </Typography>
                    <Typography sx={PAGE.helper}>
                      Configure Google Workspace as your Identity Provider
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label="Configuration Required" 
                  color="warning" 
                  size="small" 
                />
              </Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                Google Workspace configuration will be available in a future update. Please contact your administrator for setup assistance.
              </Alert>
            </Box>
          )}

          {selectedIDP === 'ping' && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#00a651', mr: 2, width: 36, height: 36 }}>
                    <ShieldIcon />
                  </Avatar>
                  <Box>
                    <Typography sx={PAGE.subsectionTitle}>
                      Ping Identity Configuration
                    </Typography>
                    <Typography sx={PAGE.helper}>
                      Configure Ping Identity as your Identity Provider
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label="Configuration Required" 
                  color="warning" 
                  size="small" 
                />
              </Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                Ping Identity configuration will be available in a future update. Please contact your administrator for setup assistance.
              </Alert>
            </Box>
          )}

          {selectedIDP === 'generic_saml' && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#6c757d', mr: 2, width: 36, height: 36 }}>
                    <SettingsIcon />
                  </Avatar>
                  <Box>
                    <Typography sx={PAGE.subsectionTitle}>
                      Generic SAML Provider Configuration
                    </Typography>
                    <Typography sx={PAGE.helper}>
                      Configure a generic SAML Identity Provider
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label="Configuration Required" 
                  color="warning" 
                  size="small" 
                />
              </Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                Generic SAML provider configuration will be available in a future update. Please contact your administrator for setup assistance.
              </Alert>
            </Box>
          )}

          {selectedIDP === 'generic_oidc' && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#6c757d', mr: 2, width: 36, height: 36 }}>
                    <SettingsIcon />
                  </Avatar>
                  <Box>
                    <Typography sx={PAGE.subsectionTitle}>
                      Generic OIDC Provider Configuration
                    </Typography>
                    <Typography sx={PAGE.helper}>
                      Configure a generic OIDC Identity Provider
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label="Configuration Required" 
                  color="warning" 
                  size="small" 
                />
              </Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                Generic OIDC provider configuration will be available in a future update. Please contact your administrator for setup assistance.
              </Alert>
            </Box>
          )}
        </Box>
    </Box>
  );

  const renderAdvancedTab = () => (
    <Box sx={{ p: 3 }}>
      <Card variant="outlined" sx={{ ...CARD_SX, mb: 3 }}>
        <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'secondary.main', mr: 1.5, width: 36, height: 36 }}>
              <SettingsIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography sx={PAGE.subsectionTitle}>Advanced Security Settings</Typography>
              <Typography sx={PAGE.helper}>
                Configure secret sharing and advanced security options
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ ...PAGE.subsectionTitle, mb: 2 }}>
            Client Secret Sharing Configuration
          </Typography>
          <Typography sx={{ ...PAGE.helper, mb: 3 }}>
            Configure who can access client secrets when OIDC applications are created in Azure AD.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...PAGE.subsectionTitle, mb: 1.5 }}>
                  Application Owner Access
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={secretSharingSettings.shareWithOwner}
                      onChange={(e) => {
                        if (e.target.checked && !secretSharingSettings.storeInVault) return;
                        handleSecretSharingChange('shareWithOwner', e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label="Share client secret with application owners"
                />
                <Typography sx={{ ...PAGE.helper, mt: 1 }}>
                  List responses stay redacted. With vault storage on, an owner can check out the client secret. That checkout is written to the audit log. The IdP token is never sent to the browser.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...PAGE.subsectionTitle, mb: 1.5 }}>
                  SSO Admin Access
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={secretSharingSettings.shareWithSSOAdmin}
                      onChange={(e) => {
                        if (e.target.checked && !secretSharingSettings.storeInVault) return;
                        handleSecretSharingChange('shareWithSSOAdmin', e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label="Share client secret with SSO admins"
                />
                <Typography sx={{ ...PAGE.helper, mt: 1 }}>
                  SSO admins can check out the client secret when vault storage is on. The IdP token stays on the server.
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...PAGE.subsectionTitle, mb: 1.5 }}>
                  Vault storage
                </Typography>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={secretSharingSettings.storeInVault} 
                      onChange={(e) => handleSecretSharingChange('storeInVault', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Store client secrets in the vault"
                />
                <Typography sx={{ ...PAGE.helper, mt: 1 }}>
                  New and renewed client secrets are copied into the vault. Checkout is the only way to read one, and each checkout is audited.
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography sx={PAGE.body}>
              <strong>Security Note:</strong> Client secrets are sensitive credentials. Only enable sharing with trusted users who need access for application configuration and troubleshooting.
            </Typography>
          </Alert>
        </Box>
      </Card>

      <Card variant="outlined" sx={{ ...CARD_SX }}>
        <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'secondary.main', mr: 1.5, width: 36, height: 36 }}>
              <SettingsIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography sx={PAGE.subsectionTitle}>Onboarding Automation Settings</Typography>
              <Typography sx={PAGE.helper}>
                Configure automated onboarding and approval workflows
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ ...PAGE.subsectionTitle, mb: 2 }}>
            Automation Configuration
          </Typography>
          <Typography sx={{ ...PAGE.helper, mb: 3 }}>
            Configure how applications are automatically onboarded and approved in the system.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl>
                <FormLabel id="onboarding-mode-label">Onboarding mode</FormLabel>
                <RadioGroup
                  aria-labelledby="onboarding-mode-label"
                  value={automationSettings.autoOnboarding && !automationSettings.ssoAdminApproval ? 'direct' : 'approval'}
                  onChange={(e) => {
                    const direct = e.target.value === 'direct';
                    const next = {
                      ...automationSettings,
                      autoOnboarding: direct,
                      ssoAdminApproval: !direct
                    };
                    setAutomationSettings(next);
                    void (async () => {
                      try {
                        if (!hasAuthSession()) return;
                        const response = await fetch(`${API_ROOT}/entra/automation-settings`, {
                          method: 'PUT',
                          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
                          body: JSON.stringify({
                            settings: {
                              auto_onboarding: next.autoOnboarding,
                              sso_admin_approval: next.ssoAdminApproval,
                              email_notifications: next.emailNotifications,
                              service_now_integration: false
                            }
                          })
                        });
                        if (!response.ok) setAutomationSettings(automationSettings);
                      } catch {
                        setAutomationSettings(automationSettings);
                      }
                    })();
                  }}
                >
                  <FormControlLabel value="approval" control={<Radio />} label="Owners submit, and an SSO admin approves before onboarding" />
                  <FormControlLabel value="direct" control={<Radio />} label="Owners onboard directly, without a separate approval" />
                </RadioGroup>
                <Typography sx={{ ...PAGE.helper, mt: 1 }}>
                  One mode is active at a time. Approval stays the effective mode when both older switches were on.
                </Typography>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...PAGE.subsectionTitle, mb: 1.5 }}>
                  Email Notifications
                </Typography>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={automationSettings.emailNotifications} 
                      onChange={(e) => handleAutomationChange('emailNotifications', e.target.checked)}
                      color="primary" 
                    />
                  }
                  label="Save the email preference"
                />
                <Typography sx={{ ...PAGE.helper, mt: 1 }}>
                  This preference is saved on the tenant. No email is sent.
                </Typography>
              </Box>
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography sx={PAGE.body}>
              <strong>Automation Note:</strong> These settings control the automated workflows for application onboarding. Changes will affect all new applications and onboarding processes.
            </Typography>
          </Alert>
        </Box>
      </Card>
    </Box>
  );

  const renderTabContent = (tabIndex: number) => {
    switch (tabIndex) {
      case 0:
        return renderSSOTab();
      case 1:
        return (
          <Box sx={{ p: 3 }}>
            <SSOAttributeSettings userRole={user?.role || ''} />
          </Box>
        );
      case 2:
        return renderAdvancedTab();
      default:
        return null;
    }
  };

  return (
    <Box sx={PAGE.padding}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <SecurityIcon sx={{ color: 'primary.main', fontSize: 22 }} />
        <Box>
          <Typography sx={PAGE.title}>Security Settings</Typography>
          <Typography sx={PAGE.subtitle}>
            Configure identity providers, governance, and automation settings
          </Typography>
        </Box>
      </Box>

      <Card variant="outlined" sx={CARD_SX}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', px: 1.5 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={TABS_SX}
          >
            <Tab label="SSO" icon={<CloudIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="SSO Attributes" icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Advanced" icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          </Tabs>
        </Box>

        {renderTabContent(activeTab)}
      </Card>
    </Box>
  );
};

export default SecuritySimple;
